import React from 'react';
import PropTypes from 'prop-types';
import { useReactToPrint } from 'react-to-print';

/**
 * Composant tampon pour l'impression.
 *
 * react-to-print v3 a remplace le composant <ReactToPrint> par le hook useReactToPrint,
 * inutilisable dans les composants classe (majoritaires ici). Ce tampon reproduit l'ancienne
 * API `<ReactToPrint trigger={() => <Button/>} content={() => node} ... />` au-dessus du hook,
 * si bien que les composants consommateurs restent inchanges et qu'une future evolution de la
 * librairie ne touchera que ce fichier.
 *
 * Props: trigger (() => element), content (() => noeud DOM a imprimer), plus toute option du hook
 * (pageStyle, documentTitle, onBeforePrint, onAfterPrint, bodyClass...).
 *
 * ATTENTION: `content` doit renvoyer un vrai noeud DOM (ou une ref qui en contient un). La v2 de la
 * librairie acceptait une instance de composant classe et la resolvait via findDOMNode, API supprimee
 * de React 19. Un ref pose sur un composant classe renvoie l'instance, pas le noeud: il faut donc
 * entourer le contenu d'un <div ref={...}>.
 */
const resolveNode = value => {
    const node = value && typeof value === 'object' && 'current' in value ? value.current : value;
    return node && typeof node.cloneNode === 'function' ? node : null;
};

export default function ReactToPrint({ trigger, content, ...options }) {
    const handlePrint = useReactToPrint(options);
    return React.cloneElement(trigger(), {
        onClick: event => {
            if(event && typeof event.preventDefault === 'function') event.preventDefault();
            const node = resolveNode(content());
            if(!node) {
                console.error('ReactToPrint: `content` doit renvoyer un noeud DOM (posez la ref sur un <div> englobant, pas sur un composant classe).');
                return;
            }
            handlePrint(() => node);
        },
    });
}

ReactToPrint.propTypes = {
    trigger: PropTypes.func.isRequired,
    content: PropTypes.func.isRequired,
};
